import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { Textarea } from "design-system/atoms/Textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Loading } from "modules/shared/components/Loading"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityResponseDataSchema,
  UpdateOrderProvider,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilityProvider } from "../../hooks/useCapabilityProvider"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: {
    provider?: string
    activationInstructions?: string
    responseInstructions?: string
    isShippingAddressUpdateEnabled?: boolean
    isRecipientNameUpdateEnabled?: boolean
    isOnHoldEnabled?: boolean
    isDeliveryPriorityUpdateEnabled?: boolean
    isProductUpdateEnabled?: boolean
  }
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function UpdateOrderForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const [activationInstructions, setActivationInstructions] = useState(
    initialConfig?.activationInstructions ?? "",
  )
  const [responseInstructions, setResponseInstructions] = useState(
    initialConfig?.responseInstructions ?? "",
  )
  const [isShippingAddressUpdateEnabled, setIsShippingAddressUpdateEnabled] = useState(
    initialConfig?.isShippingAddressUpdateEnabled ?? true,
  )
  const [isRecipientNameUpdateEnabled, setIsRecipientNameUpdateEnabled] = useState(
    initialConfig?.isRecipientNameUpdateEnabled ?? true,
  )
  const [isOnHoldEnabled, setIsOnHoldEnabled] = useState(initialConfig?.isOnHoldEnabled ?? true)
  const [isDeliveryPriorityUpdateEnabled, setIsDeliveryPriorityUpdateEnabled] = useState(
    initialConfig?.isDeliveryPriorityUpdateEnabled ?? true,
  )
  const [isProductUpdateEnabled, setIsProductUpdateEnabled] = useState(
    initialConfig?.isProductUpdateEnabled ?? false,
  )
  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()

  const isPending = mode === "install" ? isInstallPending : isUpdatePending

  const { provider, setProvider, providerOptions, isIntegrationsStatusPending } =
    useCapabilityProvider({
      capability,
      mode,
      initialConfig,
    })
  const hasLogisticsProvider = providerOptions.some(
    ({ value, disabled }) =>
      !disabled && (value === UpdateOrderProvider.BigBlue || value === UpdateOrderProvider.Amphora),
  )
  const visibleProviderOptions = providerOptions.filter(
    ({ value }) => value !== UpdateOrderProvider.Shopify || !hasLogisticsProvider,
  )
  const isShopifyProvider = provider === UpdateOrderProvider.Shopify
  const isOnHoldAvailable = provider === UpdateOrderProvider.Amphora

  useEffect(() => {
    onPendingChange?.(isPending || isIntegrationsStatusPending)
  }, [isPending, isIntegrationsStatusPending, onPendingChange])

  useEffect(() => {
    setActivationInstructions(initialConfig?.activationInstructions ?? "")
  }, [initialConfig?.activationInstructions])

  useEffect(() => {
    setResponseInstructions(initialConfig?.responseInstructions ?? "")
  }, [initialConfig?.responseInstructions])

  useEffect(() => {
    setIsShippingAddressUpdateEnabled(initialConfig?.isShippingAddressUpdateEnabled ?? true)
  }, [initialConfig?.isShippingAddressUpdateEnabled])

  useEffect(() => {
    setIsRecipientNameUpdateEnabled(initialConfig?.isRecipientNameUpdateEnabled ?? true)
  }, [initialConfig?.isRecipientNameUpdateEnabled])

  useEffect(() => {
    setIsOnHoldEnabled(initialConfig?.isOnHoldEnabled ?? true)
  }, [initialConfig?.isOnHoldEnabled])

  useEffect(() => {
    setIsDeliveryPriorityUpdateEnabled(initialConfig?.isDeliveryPriorityUpdateEnabled ?? true)
  }, [initialConfig?.isDeliveryPriorityUpdateEnabled])

  useEffect(() => {
    if (!isShopifyProvider) return

    setIsOnHoldEnabled(false)
    setIsDeliveryPriorityUpdateEnabled(false)
  }, [isShopifyProvider])

  useEffect(() => {
    if (!hasLogisticsProvider || !isShopifyProvider) return

    setProvider(
      providerOptions.find(
        ({ value, disabled }) =>
          !disabled &&
          (value === UpdateOrderProvider.BigBlue || value === UpdateOrderProvider.Amphora),
      )?.value,
    )
  }, [hasLogisticsProvider, isShopifyProvider, providerOptions, setProvider])

  useEffect(() => {
    setIsProductUpdateEnabled(initialConfig?.isProductUpdateEnabled ?? false)
  }, [initialConfig?.isProductUpdateEnabled])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      if (
        !isShippingAddressUpdateEnabled &&
        !isRecipientNameUpdateEnabled &&
        !(isOnHoldAvailable && isOnHoldEnabled) &&
        !(isShopifyProvider ? false : isDeliveryPriorityUpdateEnabled) &&
        !isProductUpdateEnabled
      ) {
        toast.error(t(`capabilities.catalog.${capability.identifier}.config.actions.minRequired`))
        return
      }

      const config = {
        provider,
        activationInstructions,
        responseInstructions,
        isShippingAddressUpdateEnabled,
        isRecipientNameUpdateEnabled,
        isOnHoldEnabled: isOnHoldAvailable && isOnHoldEnabled,
        isDeliveryPriorityUpdateEnabled: isShopifyProvider
          ? false
          : isDeliveryPriorityUpdateEnabled,
        isProductUpdateEnabled,
      }

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: capability.identifier,
          config,
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t(`capabilities.catalog.${capability.identifier}.name`),
          }),
        )
      } else {
        await updateCapabilityConfig({
          id: capability.id,
          config,
        })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      isPending,
      mode,
      onConfigure,
      installCapability,
      capability.identifier,
      capability.id,
      provider,
      isShippingAddressUpdateEnabled,
      isRecipientNameUpdateEnabled,
      isOnHoldEnabled,
      isDeliveryPriorityUpdateEnabled,
      isShopifyProvider,
      isOnHoldAvailable,
      isProductUpdateEnabled,
      t,
      updateCapabilityConfig,
      onClose,
      activationInstructions,
      responseInstructions,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  if (isIntegrationsStatusPending) return <Loading />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <Label>{t(`capabilities.catalog.${capability.identifier}.config.provider.label`)}</Label>
        <Select value={provider ?? undefined} onValueChange={setProvider}>
          <SelectTrigger>
            <SelectValue
              placeholder={t(
                `capabilities.catalog.${capability.identifier}.config.provider.placeholder`,
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {visibleProviderOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <section className="flex flex-col gap-3 border-t pt-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="update-order-shipping-address-enabled">
              {t(`capabilities.catalog.${capability.identifier}.config.actions.shippingAddress`)}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t(
                `capabilities.catalog.${capability.identifier}.config.actions.shippingAddressDescription`,
              )}
            </p>
          </div>
          <Switch
            id="update-order-shipping-address-enabled"
            checked={isShippingAddressUpdateEnabled}
            onCheckedChange={setIsShippingAddressUpdateEnabled}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="update-order-recipient-name-enabled">
              {t(`capabilities.catalog.${capability.identifier}.config.actions.recipientName`)}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t(
                `capabilities.catalog.${capability.identifier}.config.actions.recipientNameDescription`,
              )}
            </p>
          </div>
          <Switch
            id="update-order-recipient-name-enabled"
            checked={isRecipientNameUpdateEnabled}
            onCheckedChange={setIsRecipientNameUpdateEnabled}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="update-order-on-hold-enabled">
              {t(`capabilities.catalog.${capability.identifier}.config.actions.onHold`)}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t(`capabilities.catalog.${capability.identifier}.config.actions.onHoldDescription`)}
            </p>
          </div>
          <Switch
            id="update-order-on-hold-enabled"
            checked={isOnHoldAvailable && isOnHoldEnabled}
            onCheckedChange={setIsOnHoldEnabled}
            disabled={!isOnHoldAvailable}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="update-order-delivery-priority-enabled">
              {t(`capabilities.catalog.${capability.identifier}.config.actions.deliveryPriority`)}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t(
                `capabilities.catalog.${capability.identifier}.config.actions.deliveryPriorityDescription`,
              )}
            </p>
          </div>
          <Switch
            id="update-order-delivery-priority-enabled"
            checked={isShopifyProvider ? false : isDeliveryPriorityUpdateEnabled}
            onCheckedChange={setIsDeliveryPriorityUpdateEnabled}
            disabled={isShopifyProvider}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="update-order-product-enabled">
              {t(`capabilities.catalog.${capability.identifier}.config.actions.product`)}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t(`capabilities.catalog.${capability.identifier}.config.actions.productDescription`)}
            </p>
          </div>
          <Switch
            id="update-order-product-enabled"
            checked={isProductUpdateEnabled}
            onCheckedChange={setIsProductUpdateEnabled}
          />
        </div>
      </section>
      <section className="flex flex-col gap-4 border-t pt-4">
        <div className="flex flex-col gap-1.5">
          <Label>
            {t(`capabilities.catalog.${capability.identifier}.config.activationInstructions.label`)}
          </Label>
          <Textarea
            value={activationInstructions}
            onChange={(ev) => setActivationInstructions(ev.target.value)}
            placeholder={t(
              `capabilities.catalog.${capability.identifier}.config.activationInstructions.placeholder`,
            )}
            rows={5}
            maxLength={2048}
          />
          <p className="text-sm text-muted-foreground">
            {t(
              `capabilities.catalog.${capability.identifier}.config.activationInstructions.description`,
            )}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>
            {t(`capabilities.catalog.${capability.identifier}.config.responseInstructions.label`)}
          </Label>
          <Textarea
            value={responseInstructions}
            onChange={(ev) => setResponseInstructions(ev.target.value)}
            placeholder={t(
              `capabilities.catalog.${capability.identifier}.config.responseInstructions.placeholder`,
            )}
            rows={5}
            maxLength={2048}
          />
          <p className="text-sm text-muted-foreground">
            {t(
              `capabilities.catalog.${capability.identifier}.config.responseInstructions.description`,
            )}
          </p>
        </div>
      </section>
    </div>
  )
}
